/**
 * « Les affinités » — what the thinking phase of a generation shows.
 *
 * On the left, what Du Chef knows about the home; on the right, one candidate
 * at a time for a single witness slot. A wire lights for every fact the
 * candidate honours and a gauge fills; the one that honours the most stays.
 * Then the panel steps aside and the existing cascade fills the week.
 *
 * It computes nothing — it plays an AffinityScript read off the real plan
 * (see affinityScript.ts). Its timers are its own: the Redux timeline only
 * decides when thinking ends (THINK_MS), so a cancel that unmounts the panel
 * leaves nothing behind.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Image, LayoutChangeEvent, Platform, StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Paprika, PaprikaShadows } from '../../styles/theme';
import { RemoteImage } from '../shared/RemoteImage';
import { resolveRecipeTitle } from '../../utils/recipeTitle';
import { ALLERGENS } from '../../data/allergens';
import type { DietType } from '../../types/recipe';
import { useReduceMotion } from '../../hooks/useReduceMotion';
import {
  dayFullKey,
  AFFINITY_FIRST_MS, AFFINITY_STEP_MS, AFFINITY_HIT_DELAY_MS, AFFINITY_HIT_MS,
  AFFINITY_METER_DELAY_MS, AFFINITY_GONE_MS, AFFINITY_WON_MS, AFFINITY_COUNT_MS,
  AFFINITY_PANEL_OUT_MS, AFFINITY_FACT_STEP_MS,
} from '../weekly/v2/constants';
import type { AffinityFact, AffinityScript } from './affinityScript';

const NATIVE = Platform.OS !== 'web';
/** The chef in his roundel: it is Du Chef doing this sorting, and saying so. */
const CHEF = require('../../../assets/brand/pastille/pastille-64.png');
const EASE_IN = Easing.bezier(0.22, 0.7, 0.25, 1);
const EASE_METER = Easing.bezier(0.4, 0, 0.2, 1);
const WIRE_DRAW_MS = 500;

/** French writes day names in lower case mid-sentence (« gardé pour mercredi »). */
function dayName(t: TFunction, lang: string, di: number): string {
  const name = t(dayFullKey(di));
  return lang.startsWith('fr') ? name.toLocaleLowerCase('fr') : name;
}

function dietLabel(t: TFunction, diet: DietType): string {
  switch (diet) {
    case 'vegetarian': return t('diet.vegetarian');
    case 'vegan': return t('diet.vegan');
    case 'gluten-free': return t('diet.glutenFree');
    case 'dairy-free': return t('diet.dairyFree');
    default: return diet;
  }
}

function kindLabel(t: TFunction, kind: string): string {
  if (kind === 'fish') return t('mealConfig.affinity.kindFish');
  if (kind === 'meat') return t('mealConfig.affinity.kindMeat');
  return t('mealConfig.affinity.kindVeg');
}

export function factLabel(t: TFunction, lang: string, fact: AffinityFact): string {
  switch (fact.key) {
    case 'household': {
      const { rule } = fact;
      if (rule.source === 'diet') return dietLabel(t, rule.value);
      const item = rule.source === 'allergen'
        ? ALLERGENS[rule.value].label[lang.startsWith('fr') ? 'fr' : 'en']
        : rule.value;
      return t('mealConfig.affinity.factAvoid', { item: lang.startsWith('fr') ? item.toLocaleLowerCase('fr') : item.toLowerCase() });
    }
    case 'pantry':
      return t('mealConfig.affinity.factPantry', { items: fact.items.join(', ') });
    case 'time':
      return t('mealConfig.affinity.factTime', { day: t(dayFullKey(fact.di)), minutes: fact.minutes });
    case 'history':
      return t('mealConfig.affinity.factHistory');
    case 'diet':
      return t('mealConfig.affinity.factDiet', { kind: kindLabel(t, fact.kind), count: fact.count });
  }
}

// ─── Wires ────────────────────────────────────────────────────────────────────

/** Length of the cubic from (0,y) to (w,h/2), sampled — dash maths needs pixels. */
function curveLength(w: number, y: number, h: number): number {
  const p = (s: number) => {
    const u = 1 - s;
    const x = 3 * u * u * s * (0.42 * w) + 3 * u * s * s * (0.58 * w) + s * s * s * w;
    const yy = u * u * u * y + 3 * u * u * s * y + 3 * u * s * s * (h / 2) + s * s * s * (h / 2);
    return [x, yy];
  };
  let len = 0;
  let [px, py] = p(0);
  for (let i = 1; i <= 24; i++) {
    const [x, yy] = p(i / 24);
    len += Math.hypot(x - px, yy - py);
    px = x; py = yy;
  }
  return len;
}

interface WiresProps {
  /** Chip centres, px from the top of the row. */
  ys: (number | undefined)[];
  /** When each wire lit (performance.now()), or null when unlit. */
  litAt: (number | null)[];
  instant: boolean;
}

/**
 * One curve per fact, from its chip to the middle of the candidate. A lit wire
 * draws itself left to right; the frame loop runs only while one is drawing.
 */
function Wires({ ys, litAt, instant }: WiresProps) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [, setFrame] = useState(0);

  useEffect(() => {
    if (instant) return;
    const drawing = () => litAt.some((at) => at != null && performance.now() - at < WIRE_DRAW_MS);
    if (!drawing()) return;
    let raf = 0;
    const step = () => {
      setFrame((f) => f + 1);
      if (drawing()) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [litAt, instant]);

  const onLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setSize({ w: width, h: height });
  };

  const { w, h } = size;
  return (
    <View style={styles.wires} onLayout={onLayout} pointerEvents="none">
      {w > 0 && (
        <Svg width={w} height={h}>
          {ys.map((y, i) => {
            if (y == null) return null;
            const d = `M0,${y} C${0.42 * w},${y} ${0.58 * w},${h / 2} ${w},${h / 2}`;
            const at = litAt[i];
            if (at == null) {
              return <Path key={i} d={d} stroke={Paprika.border} strokeWidth={1.1} strokeOpacity={0.6} fill="none" />;
            }
            const len = curveLength(w, y, h);
            const p = instant ? 1 : Math.min(1, (performance.now() - at) / WIRE_DRAW_MS);
            const eased = 1 - Math.pow(1 - p, 2);
            return (
              <React.Fragment key={i}>
                <Path d={d} stroke={Paprika.border} strokeWidth={1.1} strokeOpacity={0.6} fill="none" />
                <Path
                  d={d}
                  stroke={Paprika.primary}
                  strokeWidth={1.1}
                  fill="none"
                  strokeDasharray={[len, len]}
                  strokeDashoffset={len * (1 - eased)}
                />
              </React.Fragment>
            );
          })}
        </Svg>
      )}
    </View>
  );
}

// ─── Chip ─────────────────────────────────────────────────────────────────────

function Chip({ label, lit, compact, onCentre }: {
  label: string; lit: boolean; compact: boolean; onCentre: (y: number) => void;
}) {
  const v = useRef(new Animated.Value(lit ? 1 : 0)).current;
  useEffect(() => {
    Animated.timing(v, { toValue: lit ? 1 : 0, duration: 250, useNativeDriver: false }).start();
  }, [lit, v]);
  const backgroundColor = v.interpolate({ inputRange: [0, 1], outputRange: [Paprika.surfaceLow, Paprika.brandTint] });
  const borderColor = v.interpolate({ inputRange: [0, 1], outputRange: [Paprika.borderLight, Paprika.brandSoft] });
  const color = v.interpolate({ inputRange: [0, 1], outputRange: [Paprika.textMuted, Paprika.primary] });
  return (
    <Animated.View
      style={[styles.chip, compact && styles.chipCompact, { backgroundColor, borderColor }]}
      onLayout={(e) => onCentre(e.nativeEvent.layout.y + e.nativeEvent.layout.height / 2)}
      testID="affinity-fact"
      accessibilityState={{ selected: lit }}
    >
      <Animated.View style={[styles.chipDot, { backgroundColor: color }]} />
      <Animated.Text
        style={[styles.chipText, compact && styles.chipTextCompact, lit && styles.chipTextLit, { color }]}
        numberOfLines={2}
      >
        {label}
      </Animated.Text>
    </Animated.View>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

interface Props {
  script: AffinityScript;
  /** Mobile: full width minus the screen gutters, tighter paddings. */
  compact?: boolean;
}

export function GenerationAffinityPanel({ script, compact = false }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language ?? 'fr';
  const reduced = useReduceMotion();
  const reducedRef = useRef(reduced);
  reducedRef.current = reduced;

  const { facts, candidates, slot } = script;
  const hasCandidates = candidates.length > 0;

  const [candIndex, setCandIndex] = useState(-1);
  const [won, setWon] = useState(false);
  const [litAt, setLitAt] = useState<(number | null)[]>(() => facts.map(() => null));
  const [count, setCount] = useState(0);
  const [ys, setYs] = useState<(number | undefined)[]>(() => facts.map(() => undefined));

  const panel = useRef(new Animated.Value(0)).current;       // 0 → 1 in, 1 → 2 out
  const cardIn = useRef(new Animated.Value(0)).current;
  const cardOut = useRef(new Animated.Value(0)).current;
  const meter = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));
    let raf = 0;

    Animated.timing(panel, { toValue: 1, duration: 340, easing: EASE_IN, useNativeDriver: NATIVE }).start();

    // The counter climbs ease-out to the real number; reduced motion shows it at once.
    const start = Date.now();
    const climb = () => {
      const k = reducedRef.current ? 1 : Math.min(1, (Date.now() - start) / AFFINITY_COUNT_MS);
      setCount(Math.round(script.reviewed * (1 - Math.pow(1 - k, 2))));
      if (k < 1) raf = requestAnimationFrame(climb);
    };
    raf = requestAnimationFrame(climb);

    const light = (i: number) => setLitAt((prev) => prev.map((v, j) => (j === i ? performance.now() : v)));
    const unlightAll = () => setLitAt(facts.map(() => null));

    if (!hasCandidates) {
      // Too few recipes for runners-up: the facts light one after another.
      facts.forEach((_, i) => at(AFFINITY_FIRST_MS + i * AFFINITY_FACT_STEP_MS, () => light(i)));
    }

    candidates.forEach((c, k) => {
      const t0 = AFFINITY_FIRST_MS + k * AFFINITY_STEP_MS;
      const isWinner = k === candidates.length - 1;
      at(t0, () => {
        unlightAll();
        setCandIndex(k);
        cardOut.setValue(0);
        cardIn.setValue(0);
        meter.setValue(0);
        Animated.timing(cardIn, {
          toValue: 1,
          duration: reducedRef.current ? 200 : 290,
          easing: EASE_IN,
          useNativeDriver: NATIVE,
        }).start();
      });
      c.hits.forEach((h, j) => at(
        t0 + AFFINITY_HIT_DELAY_MS + (reducedRef.current ? 0 : j * AFFINITY_HIT_MS),
        () => light(h),
      ));
      at(t0 + AFFINITY_METER_DELAY_MS, () => {
        if (reducedRef.current) meter.setValue(c.score);
        else Animated.timing(meter, { toValue: c.score, duration: 420, easing: EASE_METER, useNativeDriver: false }).start();
      });
      if (isWinner) {
        at(t0 + AFFINITY_WON_MS, () => setWon(true));
      } else {
        at(t0 + AFFINITY_GONE_MS, () => {
          Animated.timing(cardOut, { toValue: 1, duration: reducedRef.current ? 200 : 300, useNativeDriver: NATIVE }).start();
        });
      }
    });

    at(AFFINITY_PANEL_OUT_MS, () => {
      Animated.timing(panel, { toValue: 2, duration: 300, useNativeDriver: NATIVE }).start();
    });

    return () => {
      timers.forEach(clearTimeout);
      cancelAnimationFrame(raf);
    };
    // The script is fixed for the life of the panel: a new generation remounts it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const candidate = candIndex >= 0 ? candidates[candIndex] : undefined;
  const day = dayName(t, lang, slot.di);
  const labels = useMemo(() => facts.map((f) => factLabel(t, lang, f)), [facts, t, lang]);

  const panelStyle = {
    opacity: panel.interpolate({ inputRange: [0, 1, 2], outputRange: [0, 1, 0] }),
    transform: [{ scale: panel.interpolate({ inputRange: [0, 1, 2], outputRange: [reduced ? 1 : 0.96, 1, reduced ? 1 : 1.03] }) }],
  };
  const cardStyle = {
    opacity: Animated.multiply(cardIn, Animated.subtract(1, cardOut)),
    transform: reduced ? [] : [
      { translateX: cardOut.interpolate({ inputRange: [0, 1], outputRange: [0, 26] }) },
      { scale: Animated.add(
        cardIn.interpolate({ inputRange: [0, 1], outputRange: [0.95, 1] }),
        cardOut.interpolate({ inputRange: [0, 1], outputRange: [0, -0.05] }),
      ) },
    ],
  };
  const meterWidth = meter.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] });

  return (
    <Animated.View
      style={[styles.panel, compact && styles.panelCompact, panelStyle]}
      testID="generation-affinity-panel"
      accessibilityRole="progressbar"
      accessibilityLabel={t('mealConfig.affinity.title')}
    >
      <View style={styles.head}>
        <Image source={CHEF} style={styles.chef} accessibilityIgnoresInvertColors />
        <Text style={styles.title} testID="affinity-title">{t('mealConfig.affinity.title')}</Text>
      </View>

      <View style={[styles.aff, compact && styles.affCompact, !hasCandidates && styles.affNoCand]}>
        <View style={[
          styles.you,
          compact && styles.youCompact,
          facts.length < 4 && styles.youFew,
          !hasCandidates && styles.youNoCand,
        ]}>
          {facts.map((_, i) => (
            <Chip
              key={i}
              label={labels[i]}
              lit={litAt[i] != null}
              compact={compact}
              onCentre={(y) => setYs((prev) => (prev[i] === y ? prev : prev.map((v, j) => (j === i ? y : v))))}
            />
          ))}
        </View>

        {hasCandidates && (
          <>
            <Wires ys={ys} litAt={litAt} instant={reduced} />
            <View style={[styles.cardRing, compact && styles.cardRingCompact, won && styles.cardRingWon]}>
              <Animated.View
                style={[styles.card, won && styles.cardWon, cardStyle]}
                testID="affinity-candidate"
                accessibilityState={{ selected: won }}
              >
                <View style={[styles.photo, compact && styles.photoCompact]}>
                  {candidate?.image ? (
                    <RemoteImage
                      key={candidate.recipeId}
                      uri={candidate.image}
                      variant="thumb"
                      style={StyleSheet.absoluteFill as any}
                      resizeMode="cover"
                      fallback={<View style={styles.photoFallback} />}
                    />
                  ) : <View style={styles.photoFallback} />}
                </View>
                <Text style={styles.candTitle} numberOfLines={2}>{candidate ? resolveRecipeTitle(candidate, lang) : ''}</Text>
                <View style={styles.meter}>
                  <Animated.View style={[styles.meterFill, won && styles.meterFillWon, { width: meterWidth }]} />
                </View>
                <Text style={[styles.meterLabel, won && styles.meterLabelWon]} numberOfLines={1}>
                  {won ? t('mealConfig.affinity.keptFor', { day }) : t('mealConfig.affinity.resembles')}
                </Text>
              </Animated.View>
            </View>
          </>
        )}
      </View>

      <View style={styles.foot}>
        <Text style={styles.footText} testID="affinity-reviewed">
          <Text style={styles.footCount}>{count}</Text>
          {' '}{t('mealConfig.affinity.reviewedLabel', { count: script.reviewed })}
        </Text>
        {hasCandidates && (
          <Text style={styles.footText} testID="affinity-kept">
            {won
              ? t('mealConfig.affinity.kept', { count: script.kept })
              : t('mealConfig.affinity.finalists', { count: candidates.length })}
          </Text>
        )}
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  panel: {
    width: 410,
    maxWidth: '100%',
    backgroundColor: Paprika.cardBg,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(215, 194, 184, 0.4)',
    paddingTop: 18,
    paddingHorizontal: 20,
    paddingBottom: 14,
    ...PaprikaShadows.editorial,
  },
  panelCompact: {
    width: '100%',
    padding: 14,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
  },
  chef: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  // Du Chef speaking: Newsreader italic, as the brand voice is set.
  title: {
    flex: 1,
    fontFamily: 'Newsreader-Italic',
    fontSize: 17,
    lineHeight: 22,
    color: Paprika.textPrimary,
  },
  aff: {
    flexDirection: 'row',
    height: 132,
  },
  affCompact: {
    height: 120,
  },
  affNoCand: {
    height: undefined,
    minHeight: 64,
    justifyContent: 'center',
  },
  you: {
    width: 150,
    justifyContent: 'space-between',
  },
  youCompact: {
    width: 112,
  },
  youFew: {
    justifyContent: 'space-around',
  },
  youNoCand: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignContent: 'center',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  chipCompact: {
    paddingVertical: 3,
    borderRadius: 12,
  },
  chipDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    opacity: 0.7,
  },
  chipText: {
    fontFamily: 'Inter-Medium',
    fontSize: 10,
    lineHeight: 12,
    flexShrink: 1,
  },
  chipTextCompact: {
    fontSize: 9.5,
    lineHeight: 11,
  },
  chipTextLit: {
    fontFamily: 'Inter-SemiBold',
  },
  wires: {
    flex: 1,
    minWidth: 26,
  },
  cardRing: {
    width: 136,
    alignSelf: 'center',
    borderRadius: 14,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  // The card keeps its width on a phone; the wires take whatever is left.
  cardRingCompact: {
    width: 150,
  },
  cardRingWon: {
    borderColor: Paprika.approvalYesTint,
  },
  card: {
    backgroundColor: Paprika.cardBg,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Paprika.border,
    overflow: 'hidden',
    ...PaprikaShadows.card,
  },
  cardWon: {
    borderColor: Paprika.approvalYes,
  },
  photo: {
    height: 54,
    backgroundColor: Paprika.bgSunk,
  },
  photoCompact: {
    height: 44,
  },
  photoFallback: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: Paprika.bgSunk,
  },
  candTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 11.5,
    lineHeight: 14,
    height: 28,
    color: Paprika.textPrimary,
    marginTop: 7,
    marginHorizontal: 8,
    marginBottom: 4,
  },
  meter: {
    height: 4,
    borderRadius: 3,
    backgroundColor: Paprika.bgSunk,
    marginHorizontal: 8,
    marginTop: 2,
    overflow: 'hidden',
  },
  meterFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: Paprika.primary,
  },
  meterFillWon: {
    backgroundColor: Paprika.approvalYes,
  },
  meterLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 9.5,
    lineHeight: 12,
    color: Paprika.textMuted,
    paddingHorizontal: 8,
    paddingTop: 4,
    paddingBottom: 7,
  },
  meterLabelWon: {
    fontFamily: 'Inter-Medium',
    color: Paprika.approvalYes,
  },
  foot: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: Paprika.hairline,
    marginTop: 14,
    paddingTop: 10,
  },
  footText: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    lineHeight: 14,
    color: Paprika.textMuted,
  },
  footCount: {
    fontFamily: 'Inter-SemiBold',
    color: Paprika.textSecondary,
    fontVariant: ['tabular-nums'],
  },
});
